import { clsx } from "clsx";
import type { ReactNode } from "react";
import "./ButtonPrimitive.css";

export type ButtonAppearance = "solid" | "soft" | "outline" | "ghost" | "link";
export type ButtonTone = "brand" | "accent" | "neutral" | "danger" | "success" | "warning";
export type ButtonShape = "default" | "circle";

export type LegacyButtonVariant =
  | "primary"
  | "action"
  | "secondary"
  | "tertiary"
  | "accent"
  | "neutral"
  | "ghost"
  | "link"
  | "danger"
  | "outline-primary"
  | "outline-secondary"
  | "outline-accent"
  | "outline-neutral";

export type ButtonVariant = ButtonAppearance | LegacyButtonVariant;
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonPrimitiveProps {
  as?: "a" | "button";
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  coloredShadow?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
  children: ReactNode;
  [key: string]: unknown;
}

// ButtonTone "accent" is DaisyUI-secondary-orange (the CTA tone used via the
// appearance+tone API); LegacyButtonVariant "accent" is a DIFFERENT DaisyUI
// accent-peach (used only via the direct legacy variant string) — pre-existing
// naming divergence carried over unchanged from the DaisyUI-classed version.
const toneClasses: Record<ButtonTone, string> = {
  brand: "ds-btn-tone-brand",
  accent: "ds-btn-tone-secondary",
  neutral: "ds-btn-tone-neutral",
  danger: "ds-btn-tone-danger",
  success: "ds-btn-tone-success",
  warning: "ds-btn-tone-warning",
};

const appearanceClasses: Record<ButtonAppearance, string> = {
  solid: "ds-btn-solid",
  soft: "ds-btn-soft",
  outline: "ds-btn-outline",
  ghost: "ds-btn-ghost",
  link: "ds-btn-link",
};

const legacyVariantClasses: Record<LegacyButtonVariant, string> = {
  primary: "ds-btn-solid ds-btn-tone-brand",
  action: "ds-btn-solid ds-btn-tone-secondary",
  secondary: "ds-btn-solid ds-btn-tone-secondary",
  tertiary: "ds-btn-soft ds-btn-tone-brand",
  accent: "ds-btn-solid ds-btn-tone-accent-legacy",
  neutral: "ds-btn-solid ds-btn-tone-neutral",
  ghost: "ds-btn-ghost",
  link: "ds-btn-link",
  danger: "ds-btn-solid ds-btn-tone-danger",
  "outline-primary": "ds-btn-outline ds-btn-tone-brand",
  "outline-secondary": "ds-btn-outline ds-btn-tone-secondary",
  "outline-accent": "ds-btn-outline ds-btn-tone-accent-legacy",
  "outline-neutral": "ds-btn-outline ds-btn-tone-neutral",
};

function isAppearanceVariant(
  variant: ButtonVariant,
  tone?: ButtonTone,
): variant is ButtonAppearance {
  if ((variant === "ghost" || variant === "link") && tone === undefined) {
    return false;
  }

  return variant in appearanceClasses;
}

function getVariantClasses(variant: ButtonVariant, tone?: ButtonTone): string {
  if (isAppearanceVariant(variant, tone)) {
    return clsx(appearanceClasses[variant], toneClasses[tone ?? "accent"]);
  }

  return legacyVariantClasses[variant];
}

function isLowEmphasis(variant: ButtonVariant): boolean {
  return variant === "soft" || variant === "ghost" || variant === "link" || variant === "tertiary";
}

export function ButtonPrimitive({
  as: Component = "button",
  variant = "secondary",
  tone,
  size = "lg",
  shape = "default",
  fullWidth = false,
  coloredShadow = false,
  leftIcon,
  rightIcon,
  className,
  children,
  ...rest
}: ButtonPrimitiveProps) {
  const lowEmphasis = isLowEmphasis(variant);
  const isCircle = shape === "circle";

  const classes = clsx(
    "ds-btn inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-(weight:--fw-bold)",
    !lowEmphasis &&
      !isCircle &&
      "px-8 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl",
    lowEmphasis && "transition-colors duration-300",
    isCircle && "ds-btn-circle",
    getVariantClasses(variant, tone),
    `ds-btn-${size}`,
    fullWidth && "w-full",
    coloredShadow && !lowEmphasis && "ds-btn-colored-shadow",
    className,
  );

  return (
    <Component className={classes} data-component="ButtonPrimitive" {...rest}>
      {leftIcon}
      {children}
      {rightIcon}
    </Component>
  );
}

ButtonPrimitive.displayName = "ButtonPrimitive";
